import Axios, { AxiosInstance, AxiosError, AxiosResponse } from "axios";
import configs from '../../configs';
import { History } from 'history';
import { navigate } from '../../utils/navigateUtil';
import { AppRoutes } from '../../enums/routes';

import { store } from "../../store/configureStore";
import { toggleNotification, signoutUser } from '../../actions';
import { NotificationProps } from "../../components/shared/Notification";
import { AuthState, States, PageSizeState } from "../../interfaces/states";
import { useDispatch, useSelector } from "react-redux";
import { receiveAuth, authActions } from '../../actions/authActions';
import { AuthResponseModel } from '../../interfaces/models';

interface MFETokenData {
    token: string;
    expiryDate: string;
    editable: boolean;
    savedAt: number;
}

export class AxiosGlobal {
    private _token: string;
    private _requestType: string
    public axios: AxiosInstance;
    private history: History;

    toJSON(strOrObject: string | object): object {
        if (typeof strOrObject === "object") {
            return strOrObject;
        } else if (typeof strOrObject === "string") {
            return this.toJSON(JSON.parse(strOrObject));
        } else {
            return strOrObject;
        }
    }

    get requestType() {
        return this._requestType;
    }

    set requestType(type: string) {
        this._requestType = type;
        this.axios.defaults.headers["Content-Type"] = type;
    }

    // ADDED: Extract API key from current URL
    private getApiKeyFromURL(): string | null {
        try {
            const searchParams = new URLSearchParams(window.location.search);
            return searchParams.get('apikey');
        } catch (error) {
            return null;
        }
    }

    // ADDED: Extract dashboard ID from current URL path
    private getDashboardIdFromURL(): string | null {
        try {
            // Assuming URL pattern like /dashboard/123 or /mfe/dashboard/123
            const pathParts = window.location.pathname.split('/');
            const dashboardIndex = pathParts.findIndex(part => part === 'dashboard');
            if (dashboardIndex !== -1 && pathParts[dashboardIndex + 1]) {
                return pathParts[dashboardIndex + 1];
            }
            return null;
        } catch (error) {
            return null;
        }
    }

    // ADDED: Generate token storage key for MFE
    private getTokenStorageKey(dashboardId: string, apiKey: string): string {
        return `mfe_token_${dashboardId}_${apiKey.substring(0, 8)}`;
    }

    // ADDED: Check if token is expired
    private isTokenExpired(expiryDate: string): boolean {
        try {
            const expiryTime = new Date(expiryDate).getTime();
            const currentTime = Date.now();
            const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
            return currentTime >= (expiryTime - bufferTime);
        } catch (error) {
            return true;
        }
    }

    // ADDED: Get saved MFE token from localStorage
    private getSavedMFEToken(): string | null {
        try {
            const apiKey = this.getApiKeyFromURL();
            const dashboardId = this.getDashboardIdFromURL();

            if (!apiKey || !dashboardId) {
                console.log('AxiosGlobal: No API key or dashboard ID found in URL');
                return null;
            }

            const storageKey = this.getTokenStorageKey(dashboardId, apiKey);
            const savedData = localStorage.getItem(storageKey);

            if (!savedData) {
                console.log('AxiosGlobal: No saved MFE token found');
                return null;
            }

            const tokenData: MFETokenData = JSON.parse(savedData);

            if (this.isTokenExpired(tokenData.expiryDate)) {
                console.log('AxiosGlobal: Saved MFE token is expired');
                localStorage.removeItem(storageKey);
                return null;
            }

            console.log('AxiosGlobal: Found valid MFE token');
            return tokenData.token;
        } catch (error) {
            console.warn('AxiosGlobal: Failed to retrieve MFE token:', error);
            return null;
        }
    }

    // ADDED: Scan localStorage for any valid MFE tokens
    private scanForMFETokens(): string | null {
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('mfe_token_')) {
                    const savedData = localStorage.getItem(key);
                    if (savedData) {
                        try {
                            const tokenData: MFETokenData = JSON.parse(savedData);
                            if (!this.isTokenExpired(tokenData.expiryDate)) {
                                console.log('AxiosGlobal: Found valid MFE token via scan:', key);
                                return tokenData.token;
                            } else {
                                // Clean up expired token
                                localStorage.removeItem(key);
                            }
                        } catch (parseError) {
                            // Clean up invalid token data
                            localStorage.removeItem(key);
                        }
                    }
                }
            }
            return null;
        } catch (error) {
            console.warn('AxiosGlobal: Failed to scan for MFE tokens:', error);
            return null;
        }
    }

    // ADDED: Method to update auth header (can be called externally)
    public updateAuthToken(token: string | null) {
        if (token) {
            this.axios.defaults.headers['Authorization'] = `Bearer ${token}`;
            console.log('AxiosGlobal: Authorization header updated');
        } else {
            delete this.axios.defaults.headers['Authorization'];
            console.log('AxiosGlobal: Authorization header removed');
        }
    }

    // ADDED: Method to refresh token from current state
    public refreshAuthToken() {
        // First check Redux store
        const tokenObj: AuthState = store.getState().auth;
        if (tokenObj?.auth?.identityToken) {
            this.updateAuthToken(tokenObj.auth.identityToken);
            return;
        }

        // Then check for MFE tokens
        const mfeToken = this.getSavedMFEToken() || this.scanForMFETokens();
        if (mfeToken) {
            this.updateAuthToken(mfeToken);
        }
    }

    constructor() {
        this.history = configs.history;
        let headers = {
            'Content-Type': 'application/json',
            callerType: 'web'
        };

        // UPDATED: Enhanced token detection logic
        console.log('AxiosGlobal: Initializing...');

        // First priority: Check Redux store for existing auth
        const tokenObj: AuthState = store.getState().auth;
        console.log('AxiosGlobal: Redux auth state:', tokenObj);

        let authToken: string | null = null;

        if (tokenObj?.auth?.identityToken) {
            authToken = tokenObj.auth.identityToken;
            console.log('AxiosGlobal: Using token from Redux store');
        } else {
            // Second priority: Check for MFE token in localStorage
            authToken = this.getSavedMFEToken();

            if (!authToken) {
                // Third priority: Scan all localStorage for any valid MFE tokens
                authToken = this.scanForMFETokens();
            }
        }


        console.log('AxiosGlobal: Final auth token:', this.getSavedMFEToken());

        if (authToken) {
            headers['Authorization'] = `Bearer ${authToken}`;
            console.log('AxiosGlobal: Authorization header set');
        } else {
            console.log('AxiosGlobal: No valid token found');
        }

        this.axios = Axios.create({
            baseURL: `${configs.context}/api`,
            timeout: configs.requestTimeOut,
            headers: headers
        });

        // Add a request interceptor
        this.axios.interceptors.request.use((config) => {
            // ADDED: Dynamic token refresh on each request
            const currentState = store.getState().auth;
            if (currentState?.auth?.identityToken && !config.headers['Authorization']) {
                config.headers['Authorization'] = `Bearer ${currentState.auth.identityToken}`;
            }
            return config;
        }, (error: AxiosError) => {
            if (error.request && error.request.data) {
                return Promise.reject(error.request);
            } else {
                return Promise.reject(error);
            }
        });

        // Add a response interceptor
        this.axios.interceptors.response.use((response: AxiosResponse) => {
            return response;
        }, (error: AxiosError) => {
            if (error && error.response && error.response.status && (error.response.status === 401)) {
                const notification: NotificationProps = {
                    theme: "danger",
                    title: "unAuthenticated",
                    message: `Please login to proceed`,
                    toggle: true,
                    onDismiss: () => { }
                };

                if (error?.response?.data?.message || error?.response?.data?.ErrorMessage) {
                    if (error.response.data.name && error.response.data.name === 'TokenExipredError') {
                        store.dispatch(signoutUser());
                    }
                    notification.message = error.response.data.message || error.response.data?.ErrorMessage;
                }
                store.dispatch(toggleNotification(notification));
                store.dispatch(signoutUser());

                navigate(AppRoutes.Account, true);
                store.dispatch(signoutUser());

                return Promise.reject({ ...error });
            } else if (error && error.response && error.response.status === 403) {
                const notification: NotificationProps = {
                    theme: "danger",
                    title: "Unauthorized",
                    message: `You dont have permisiion to access this action`,
                    toggle: true,
                    onDismiss: () => { }
                };
                if (error?.response?.data?.message || error?.response?.data?.ErrorMessage) {
                    notification.message = error.response.data.message || error.response.data.ErrorMessage;
                }
                store.dispatch(toggleNotification(notification));
                return Promise.reject({ ...error });
            } else if (error && error.response && error.response.status === 400) {
                const notification: NotificationProps = {
                    theme: "danger",
                    title: "Validation Error",
                    message: `One of the field validations failed!`,
                    toggle: true,
                    onDismiss: () => { }
                };

                const data: any = error?.response?.data;

                if (Array.isArray(data)) {
                    const arrayOfErrors = data
                        .map((item: any) => item?.ErrorMessage)
                        .filter(Boolean);
                    if (arrayOfErrors.length) notification.message = arrayOfErrors.join(", ");
                } else if (data?.ErrorMessage) {
                    notification.message = data.ErrorMessage;
                } else if (data?.errors && typeof data.errors === 'object') {
                    // ASP.NET Core ProblemDetails: { errors: { fieldName: ["msg", ...] }, title, ... }
                    const fieldMessages: string[] = [];
                    Object.entries(data.errors).forEach(([field, msgs]) => {
                        if (Array.isArray(msgs)) {
                            msgs.forEach((m: any) => fieldMessages.push(`${field}: ${String(m)}`));
                        } else if (msgs != null) {
                            fieldMessages.push(`${field}: ${String(msgs)}`);
                        }
                    });
                    notification.message = fieldMessages.length
                        ? fieldMessages.join('; ')
                        : (data.title || notification.message);
                } else if (typeof data === 'string') {
                    notification.message = data;
                } else if (data?.title) {
                    notification.message = data.title;
                }
                // Final safety net: never let a non-string slip through to the toast (would crash React).
                if (typeof notification.message !== 'string') {
                    notification.message = 'Validation error';
                }

                store.dispatch(toggleNotification(notification));
                return Promise.reject(error);

            } else if (error?.response?.status === 404) {
                const notification: NotificationProps = {
                    theme: "danger",
                    title: "Record not found",
                    message: `Record not found!`,
                    toggle: true,
                    onDismiss: () => { }
                };

                if (error && Array.isArray(error.response.data)) {
                    const arrayOfErrors = error.response.data.map((item: { ErrorMessage: string }) => item.ErrorMessage);
                    notification.message = arrayOfErrors.join(", ");
                } else if (error && error.response && error.response.data && error.response.data?.ErrorMessage) {
                    notification.message = error.response.data.ErrorMessage;
                }

                store.dispatch(toggleNotification(notification));

                return Promise.reject(error);
            } else if (error && error.response && error.response.status === 500) {
                const notification: NotificationProps = {
                    theme: "danger",
                    title: "Server Down!",
                    message: `Internal server error!`,
                    toggle: true,
                    onDismiss: () => { }
                };
                store.dispatch(toggleNotification(notification));

                return Promise.reject(error);
            } else {
                return Promise.reject(error);
            }
        });
    }
}